import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { UsersThree, IdentificationCard, ArrowLeft } from '@phosphor-icons/react';
import Typography from '../../components/Typography';
import { captureAnalytics } from '../../actions/preload';
import PortalTabs from './PortalTabs';
import PortalTeamMembers from './PortalTeamMembers';
import PortalRoutingIdentifiers from './PortalRoutingIdentifiers';
import './portalSettings.scss';

const PortalSettings = ({ isAdmin, authResolved, history }) => {
  const [activeTab, setActiveTab] = useState('team');

  // Settings are admin-only. Non-admins are bounced back to the portal; the
  // API also enforces this server-side. Only redirect once the session verify
  // has populated auth state (authResolved) — on a hard load isAdmin starts
  // false and redirecting immediately would kick admins out on refresh.
  useEffect(() => {
    if (authResolved && !isAdmin) {
      history.replace('/app/referral_portal');
    }
  }, [authResolved, isAdmin, history]);

  if (!isAdmin) return null;

  const tabs = [
    { key: 'team', label: 'Team Members', icon: <UsersThree size={16} /> },
    { key: 'identifiers', label: 'Routing Identifiers', icon: <IdentificationCard size={16} /> },
  ];

  const handleTabChange = (key) => {
    if (key !== activeTab) captureAnalytics('portal_settings_tab_changed', { tab: key });
    setActiveTab(key);
  };

  return (
    <div className="portal-settings">
      <button
        type="button"
        className="portal-settings__back"
        onClick={() => history.push('/app/referral_portal')}
      >
        <ArrowLeft size={16} />
        <span>Back to portal</span>
      </button>

      <div className="portal-settings__header">
        <Typography variant="subtitleOne" bold>Settings</Typography>
        <Typography variant="text" size="small" className="portal-settings__subtitle">
          Manage your organization&apos;s team and how referrals route to you.
        </Typography>
      </div>

      <PortalTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

      {activeTab === 'team' && <PortalTeamMembers />}
      {activeTab === 'identifiers' && <PortalRoutingIdentifiers />}
    </div>
  );
};

PortalSettings.propTypes = {
  isAdmin: PropTypes.bool,
  authResolved: PropTypes.bool,
  history: PropTypes.object.isRequired,
};

PortalSettings.defaultProps = {
  isAdmin: false,
  authResolved: false,
};

export default withRouter(connect((state) => ({
  isAdmin: state.auth.isAdmin,
  // data is set by the same reducer action that sets isAdmin, so its presence
  // means the session verify has resolved.
  authResolved: !!state.auth.data,
}))(PortalSettings));
